import {useEffect, useRef, useState, type ReactNode} from 'react';
import {Link} from 'react-router-dom';
import styled, {keyframes} from 'styled-components';
import {CodeInPlaceLink} from '../CodeInPlaceLink';

const CARDINAL = '#8c1515';

// Mirror the routes used by the hero's apply cards (HeroSplash.tsx) — the
// live application flow, not the earlier "express interest" form.
const PAI_STUDENT_APPLY_ROUTE = '/apply/pai1/student';
const PAI_SL_APPLY_ROUTE = '/apply/pai1/sl';

/** One answer a learner can pick, and the node it leads to. */
interface PrereqOption {
  /** The (first-person, hopefully funny) reaction shown on the button. */
  label: string;
  /** Id of the {@link PrereqNode} this answer routes to. */
  next: string;
}

/** A branching question in the prerequisite tree. */
interface PrereqQuestion {
  /** Discriminant marking this node as a question. */
  kind: 'question';
  /** The question itself. */
  prompt: string;
  /** The answers, each routing onward. */
  options: PrereqOption[];
}

/** A terminal outcome of the tree. */
interface PrereqResult {
  /** Discriminant marking this node as a result. */
  kind: 'result';
  /** Headline verdict. */
  title: string;
  /** The warm, human explanation. */
  body: ReactNode;
  /** When set, the result shows an "Apply" button linking to this route. */
  apply?: {
    /** The application route to link to. */
    path: string;
    /** The button label. */
    label: string;
  };
}

/** Either a question or a result. */
type PrereqNode = PrereqQuestion | PrereqResult;

/**
 * The prerequisite decision tree. Kept deliberately shallow — every path
 * reaches a result within a few quick questions. Ids are referenced by
 * {@link PrereqOption.next}; the tree always starts at `'start'`.
 *
 * Two routing principles keep the tree from being brittle:
 *
 * 1. No single answer decides an outcome in the learner's disfavor. The
 *    first question never routes to a verdict — comfort with algebra says
 *    nothing about probability or ML background, so everyone comfortable
 *    (or recovered from rusty) funnels through the same `experience`
 *    question. "Rusty" gets a follow-up, not a verdict.
 * 2. The teacher verdict requires two consistent signals: substantial
 *    probability/ML background (`experience`) AND wanting to explain it
 *    (`teach`). Knowing what a line is — or even nailing one formula —
 *    is not enough on its own.
 */
const NODES: Record<string, PrereqNode> = {
  start: {
    kind: 'question',
    prompt: 'You see the equation y = 2x + 1. How do you feel about it?',
    options: [
      {label: 'Comfortable. That’s just a line.', next: 'agents'},
      {label: 'A little rusty, to be honest.', next: 'rusty'},
    ],
  },
  rusty: {
    kind: 'question',
    prompt:
      'Rusty is normal — many of our best students start there. If you sat with y = 2x + 1 for an afternoon, could you plot it and explain what the 2 is doing?',
    options: [
      {label: 'Yes — it would come back to me quickly.', next: 'agents'},
      {label: 'Honestly, I’d want a proper refresher first.', next: 'refresh'},
    ],
  },
  agents: {
    kind: 'question',
    prompt:
      'A taste of week one. Suppose an AI agent solves a task about 30% of the time, and you let several of them try independently. As you add more agents, what happens to the chance that at least one succeeds?',
    options: [
      {
        label:
          'It rises quickly, since for the group to fail every agent has to fail.',
        next: 'experience',
      },
      {
        label:
          'I’m not sure yet, but that’s the kind of reasoning I’d like to learn.',
        next: 'perfect',
      },
    ],
  },
  experience: {
    kind: 'question',
    prompt: 'How much probability or machine learning have you studied before?',
    options: [
      {label: 'Not much. That’s why I’m here.', next: 'perfect'},
      {
        label: 'Quite a lot — probability, and some machine learning too.',
        next: 'teach',
      },
    ],
  },
  teach: {
    kind: 'question',
    prompt:
      'One more. Would you enjoy explaining an idea like maximum likelihood to someone seeing it for the first time?',
    options: [
      {label: 'Yes, very much.', next: 'teachResult'},
      {
        label: 'I know the material, but teaching makes me nervous.',
        next: 'perfect',
      },
    ],
  },
  refresh: {
    kind: 'result',
    title: 'A little review first',
    body: 'You’re close. The one thing worth shoring up before you begin is your comfort with basic algebra — lines, variables, and fractions. A few hours with a resource like Khan Academy will do it, and then you’ll be in a great position to start. We’d be glad to have you.',
  },
  perfect: {
    kind: 'result',
    title: 'This course is a good fit for you',
    body: 'You have the mathematics you need, and, just as importantly, the curiosity. What you haven’t seen yet is exactly what we’re here to teach. Come join us as a student. I think you’ll enjoy it.',
    apply: {path: PAI_STUDENT_APPLY_ROUTE, label: 'Apply to learn'},
  },
  teachResult: {
    kind: 'result',
    title: 'Consider joining us as a teacher',
    body: (
      <>
        You already have what this course teaches, which puts you in a wonderful
        position to help someone else learn it. <CodeInPlaceLink /> runs on this
        idea: roughly one volunteer teacher for every ten students. Lead a
        section, pass on what you know, and — in my experience — you’ll come to
        understand it more deeply yourself for having taught it.
      </>
    ),
    apply: {path: PAI_SL_APPLY_ROUTE, label: 'Apply to teach'},
  },
};

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to { opacity: 1; transform: translateY(0); }
`;

const Card = styled.div`
  max-width: 620px;
  margin-bottom: 3rem;
`;

const Prompt = styled.p`
  line-height: 1.35;
  margin: 0 0 1.1rem;
`;

const OptionButton = styled.button`
  display: block;
  width: 100%;
  text-align: left;
  padding: 0.8rem 0 0.8rem 1rem;
  background: none;
  border: none;
  border-left: 2px solid transparent;
  border-bottom: 1px solid #efe7e7;
  font-family: inherit;
  font-size: 0.98rem;
  color: #2b2b2b;
  cursor: pointer;
  transition:
    border-color 0.15s,
    color 0.15s,
    padding-left 0.12s;

  &:first-child {
    border-top: 1px solid #efe7e7;
  }

  &:hover {
    color: ${CARDINAL};
    border-left-color: ${CARDINAL};
    padding-left: 1.25rem;
  }

  /* Keyboard focus gets the same affordance the pointer hover gives, plus an
     explicit ring so the focused row is unmistakable. */
  &:focus-visible {
    color: ${CARDINAL};
    border-left-color: ${CARDINAL};
    outline: 2px solid ${CARDINAL};
    outline-offset: 2px;
  }
`;

const ResultTitle = styled.h3`
  margin: 0 0 0.5rem;
  color: ${CARDINAL};
`;

const ResultBody = styled.p`
  margin: 0 0 1.25rem;
  line-height: 1.5;
`;

const Controls = styled.div`
  display: flex;
  gap: 0.75rem;
  align-items: center;
  flex-wrap: wrap;
`;

const PrimaryBtn = styled(Link)`
  padding: 0.6rem 1.3rem;
  background: ${CARDINAL};
  color: #fff;
  border: none;
  border-radius: 6px;
  font-family: inherit;
  font-size: 0.95rem;
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;
  transition:
    background 0.18s,
    transform 0.15s;

  &:hover {
    background: #a01818;
    color: #fff;
    transform: translateY(-2px);
  }
`;

const GhostBtn = styled.button`
  padding: 0.5rem 0.25rem;
  background: none;
  border: none;
  color: #6b6b6b;
  font-family: inherit;
  font-size: 0.9rem;
  cursor: pointer;

  &:hover {
    color: ${CARDINAL};
    text-decoration: underline;
  }
`;

const Fade = styled.div`
  animation: ${fadeIn} 0.25s ease;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

/**
 * A short, friendly "do you have the prerequisites?" quiz. It walks the learner
 * through the {@link NODES} decision tree — a few quick questions — to one of
 * three verdicts: review a little first, well-suited student, or consider
 * teaching. History is kept so the learner can step back or restart.
 *
 * @return The interactive prerequisite-check card.
 * @throws Never.
 */
export function PrereqCheck() {
  const [history, setHistory] = useState<string[]>(['start']);
  const currentId = history[history.length - 1];
  const node = NODES[currentId];

  // Answering (or going back) replaces the step's whole subtree, destroying
  // the element that held keyboard focus. Move focus to the new step's lead
  // element (question prompt or result title) so keyboard users aren't
  // dropped back to the top of the document and screen readers announce the
  // new step. Skipped on the initial render — the quiz shouldn't steal focus
  // just by mounting.
  const promptRef = useRef<HTMLParagraphElement>(null);
  const resultTitleRef = useRef<HTMLHeadingElement>(null);
  const isFirstRenderRef = useRef(true);
  useEffect(() => {
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false;
      return;
    }
    (promptRef.current ?? resultTitleRef.current)?.focus();
  }, [currentId]);

  function choose(next: string) {
    setHistory(prev => [...prev, next]);
  }

  function back() {
    setHistory(prev => (prev.length > 1 ? prev.slice(0, -1) : prev));
  }

  function restart() {
    setHistory(['start']);
  }

  if (node.kind === 'result') {
    return (
      <Card>
        <Fade key={currentId}>
          <ResultTitle ref={resultTitleRef} tabIndex={-1}>
            {node.title}
          </ResultTitle>
          <ResultBody>{node.body}</ResultBody>
          <Controls>
            {node.apply && (
              <PrimaryBtn to={node.apply.path}>{node.apply.label}</PrimaryBtn>
            )}
            <GhostBtn type="button" onClick={restart}>
              Start over
            </GhostBtn>
          </Controls>
        </Fade>
      </Card>
    );
  }

  return (
    <Card>
      <Fade key={currentId}>
        <Prompt ref={promptRef} tabIndex={-1}>
          {node.prompt}
        </Prompt>
        {node.options.map(option => (
          <OptionButton
            key={option.label}
            type="button"
            onClick={() => {
              choose(option.next);
            }}
          >
            {option.label}
          </OptionButton>
        ))}
        {history.length > 1 && (
          <GhostBtn type="button" onClick={back}>
            <span aria-hidden="true">← </span>Back
          </GhostBtn>
        )}
      </Fade>
    </Card>
  );
}
